import { useState, useEffect, useCallback, useRef } from 'react';

/* ─────────────────────────────────────────────────────────
   useLocalStorage — sans boucle infinie
   Corrections :
   1. initialValue stabilisé via useRef (jamais recréé)
   2. readValue ne dépend plus de initialValue directement
   3. setValue n'a plus storedValue dans ses deps
   4. useEffect de sync supprimé (doublon avec useState init)
   ───────────────────────────────────────────────────────── */
export const useLocalStorage = (key, initialValue) => {
  /* Stabilise initialValue — même si l'appelant passe [] ou {} en inline */
  const initialRef = useRef(initialValue);

  const readValue = useCallback(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? JSON.parse(item) : initialRef.current;
    } catch {
      return initialRef.current;
    }
  }, [key]); // ← plus de initialValue dans les deps

  const [storedValue, setStoredValue] = useState(readValue);

  const setValue = useCallback((value) => {
    try {
      /* Accepte une fonction updater comme useState */
      setStoredValue(prev => {
        const next = value instanceof Function ? value(prev) : value;
        window.localStorage.setItem(key, JSON.stringify(next));
        /* Sync cross-onglets */
        window.dispatchEvent(new StorageEvent('storage', {
          key,
          newValue: JSON.stringify(next),
        }));
        return next;
      });
    } catch (err) {
      console.warn(`useLocalStorage: erreur écriture "${key}"`, err);
    }
  }, [key]); // ← plus de storedValue dans les deps

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialRef.current);
    } catch (err) {
      console.warn(`useLocalStorage: erreur suppression "${key}"`, err);
    }
  }, [key]);

  /* Sync cross-onglets */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === key && e.newValue !== null) {
        try { setStoredValue(JSON.parse(e.newValue)); } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  return [storedValue, setValue, removeValue];
};

/* ── Favoris ─────────────────────────────────────────────── */
export const useFavorites = () => {
  const [favorites, setFavorites, clearFavorites] = useLocalStorage('yeldi_favorites', []);

  const addFavorite = useCallback((shopId) => {
    setFavorites(prev => prev.includes(shopId) ? prev : [...prev, shopId]);
  }, [setFavorites]);

  const removeFavorite = useCallback((shopId) => {
    setFavorites(prev => prev.filter(id => id !== shopId));
  }, [setFavorites]);

  const toggleFavorite = useCallback((shopId) => {
    setFavorites(prev =>
      prev.includes(shopId) ? prev.filter(id => id !== shopId) : [...prev, shopId]
    );
  }, [setFavorites]);

  const isFavorite = useCallback((shopId) => favorites.includes(shopId), [favorites]);

  return { favorites, addFavorite, removeFavorite, toggleFavorite, isFavorite, clearFavorites, count: favorites.length };
};

/* ── Vues récentes ───────────────────────────────────────── */
export const useRecentViews = (maxItems = 10) => {
  const [recent, setRecent, clearRecent] = useLocalStorage('yeldi_recentViews', []);

  const addRecent = useCallback((shop) => {
    setRecent(prev => {
      const filtered = prev.filter(item => item.id !== shop.id);
      return [
        { id: shop.id, name: shop.name, viewedAt: new Date().toISOString() },
        ...filtered,
      ].slice(0, maxItems);
    });
  }, [setRecent, maxItems]);

  return { recent, addRecent, clearRecent, count: recent.length };
};

/* ── Préférences utilisateur ─────────────────────────────── */
const DEFAULT_PREFS = {
  defaultFilter:        'all',
  notificationsEnabled: true,
  geolocationEnabled:   true,
  language:             'fr',
};

export const useUserPrefs = () => {
  const [prefs, setPrefs] = useLocalStorage('yeldi_userPrefs', DEFAULT_PREFS);

  const updatePref = useCallback((key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  }, [setPrefs]);

  return { prefs, setPrefs, updatePref };
};